import { useState, useRef, useCallback } from 'react'
import { Link } from 'react-router-dom'

const SVG_SIZE = 400
const CX = SVG_SIZE / 2
const CY = SVG_SIZE / 2
const R = 150

function formatDecimal(v: number): string {
  return v.toFixed(3)
}

function formatRadians(a: number): string {
  const norm = ((a % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI)
  const frac = norm / Math.PI
  const fracs: [number, number, string][] = [
    [0, 1, '0'],
    [1, 6, 'π/6'], [1, 4, 'π/4'], [1, 3, 'π/3'], [1, 2, 'π/2'],
    [2, 3, '2π/3'], [3, 4, '3π/4'], [5, 6, '5π/6'], [1, 1, 'π'],
    [7, 6, '7π/6'], [5, 4, '5π/4'], [4, 3, '4π/3'], [3, 2, '3π/2'],
    [5, 3, '5π/3'], [7, 4, '7π/4'], [11, 6, '11π/6'], [2, 1, '2π'],
  ]
  for (const [num, den, label] of fracs) {
    if (Math.abs(frac - num / den) < 0.02) return label
  }
  return norm.toFixed(2)
}

const NOTABLE_ANGLES = [
  { deg: 0,   sinStr: '0',    cosStr: '1',     a: 0 },
  { deg: 30,  sinStr: '1/2',  cosStr: '√3/2',  a: Math.PI / 6 },
  { deg: 45,  sinStr: '√2/2', cosStr: '√2/2',  a: Math.PI / 4 },
  { deg: 60,  sinStr: '√3/2', cosStr: '1/2',   a: Math.PI / 3 },
  { deg: 90,  sinStr: '1',    cosStr: '0',      a: Math.PI / 2 },
  { deg: 180, sinStr: '0',    cosStr: '−1',     a: Math.PI },
  { deg: 270, sinStr: '−1',   cosStr: '0',      a: 3 * Math.PI / 2 },
]

export default function MathSin() {
  const [angle, setAngle] = useState(Math.PI / 6)
  const [isDragging, setIsDragging] = useState(false)
  const svgRef = useRef<SVGSVGElement>(null)

  const sinVal = Math.sin(angle)
  const cosVal = Math.cos(angle)
  const normalizedAngle = ((angle % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI)
  const angleDeg = Math.round(normalizedAngle * 180 / Math.PI)

  // SVG pixel coords of the point on the circle
  const px = CX + R * cosVal
  const py = CY - R * sinVal

  // Foot of the perpendicular (on x-axis)
  const footX = CX + R * cosVal

  const updateAngle = useCallback((clientX: number, clientY: number) => {
    if (!svgRef.current) return
    const rect = svgRef.current.getBoundingClientRect()
    const x = ((clientX - rect.left) / rect.width) * SVG_SIZE - CX
    const y = -(((clientY - rect.top) / rect.height) * SVG_SIZE - CY)
    setAngle(Math.atan2(y, x))
  }, [])

  const handlePointerDown = (e: React.PointerEvent<SVGSVGElement>) => {
    setIsDragging(true)
    svgRef.current?.setPointerCapture(e.pointerId)
    updateAngle(e.clientX, e.clientY)
  }
  const handlePointerMove = (e: React.PointerEvent<SVGSVGElement>) => {
    if (!isDragging) return
    updateAngle(e.clientX, e.clientY)
  }
  const handlePointerUp = () => setIsDragging(false)

  // Angle arc in SVG
  const arcR = 38
  const arcEndX = CX + arcR * cosVal
  const arcEndY = CY - arcR * sinVal
  const largeArc = normalizedAngle > Math.PI ? 1 : 0
  const arcPath = `M ${CX + arcR} ${CY} A ${arcR} ${arcR} 0 ${largeArc} 0 ${arcEndX} ${arcEndY}`

  // Angle label position (midpoint of arc)
  const arcMidAngle = normalizedAngle / 2
  const arcLabelR = arcR + 16
  const arcLabelX = CX + arcLabelR * Math.cos(arcMidAngle)
  const arcLabelY = CY - arcLabelR * Math.sin(arcMidAngle)

  const quadrant = cosVal >= 0 && sinVal >= 0 ? 'I'
    : cosVal < 0 && sinVal >= 0 ? 'II'
    : cosVal < 0 && sinVal < 0 ? 'III' : 'IV'

  const sinSq = sinVal * sinVal
  const cosSq = cosVal * cosVal

  return (
    <div className="min-h-screen bg-dark text-white">
      <div className="container mx-auto px-4 py-8" style={{ maxWidth: '1100px' }}>

        {/* Back link */}
        <Link
          to="/"
          className="text-gray-500 hover:text-gray-300 transition-colors text-sm flex items-center gap-2 w-fit mb-8"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          На главную
        </Link>

        {/* Title */}
        <div className="text-center mb-10">
          <h1 className="text-4xl font-bold mb-3">
            <span className="text-gradient">Синус и косинус</span>
          </h1>
          <p className="text-gray-400 text-lg">
            Перетащи точку на окружности — и смотри, как меняются значения
          </p>
        </div>

        {/* Main 3-column layout */}
        <div className="flex flex-col lg:flex-row gap-6 items-start justify-center">

          {/* ── Left panel: live values ── */}
          <div className="card lg:w-60 w-full lg:sticky lg:top-6 flex-shrink-0 order-2 lg:order-1">
            <div className="text-center mb-5">
              <div className="text-gray-500 text-xs uppercase tracking-wider mb-1">Угол θ</div>
              <div className="text-4xl font-bold font-mono text-white">{angleDeg}°</div>
              <div className="text-gray-500 text-sm font-mono mt-0.5">{formatRadians(angle)} рад</div>
              <div
                className="mt-2 inline-block rounded px-2.5 py-0.5 text-xs font-mono"
                style={{ background: 'rgba(132,204,22,0.1)', color: '#84cc16', border: '1px solid rgba(132,204,22,0.2)' }}
              >
                Квадрант {quadrant}
              </div>
            </div>

            <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', margin: '0 0 16px' }} />

            {/* sin */}
            <div className="mb-5">
              <div className="flex items-center gap-2 mb-1">
                <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: '#84cc16' }} />
                <span className="text-xs text-gray-500 font-mono">sin(θ)</span>
              </div>
              <div className="font-mono text-3xl font-bold" style={{ color: '#84cc16' }}>
                {formatDecimal(sinVal)}
              </div>
              <div className="text-xs text-gray-600 mt-0.5">↕ проекция на ось Y</div>
            </div>

            {/* cos */}
            <div className="mb-5">
              <div className="flex items-center gap-2 mb-1">
                <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: '#22d3ee' }} />
                <span className="text-xs text-gray-500 font-mono">cos(θ)</span>
              </div>
              <div className="font-mono text-3xl font-bold" style={{ color: '#22d3ee' }}>
                {formatDecimal(cosVal)}
              </div>
              <div className="text-xs text-gray-600 mt-0.5">↔ проекция на ось X</div>
            </div>

            <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', margin: '0 0 14px' }} />

            {/* Pythagorean identity live */}
            <div className="text-center">
              <div className="text-xs text-gray-600 mb-1">sin²θ + cos²θ = 1</div>
              <div className="font-mono text-xs text-gray-500">
                {sinSq.toFixed(3)} + {cosSq.toFixed(3)} = {(sinSq + cosSq).toFixed(3)}
              </div>
            </div>
          </div>

          {/* ── SVG unit circle ── */}
          <div className="flex-shrink-0 w-full order-1 lg:order-2" style={{ maxWidth: '420px' }}>
            <div
              className="card p-3"
              style={{ background: 'rgba(10,10,10,0.9)', border: '1px solid rgba(132,204,22,0.12)' }}
            >
              <svg
                ref={svgRef}
                viewBox={`0 0 ${SVG_SIZE} ${SVG_SIZE}`}
                style={{
                  display: 'block',
                  width: '100%',
                  cursor: isDragging ? 'grabbing' : 'crosshair',
                  touchAction: 'none',
                  userSelect: 'none',
                }}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerCancel={handlePointerUp}
              >
                {/* Subtle grid */}
                {[-1, -0.5, 0.5, 1].map(v => (
                  <g key={v}>
                    <line x1={CX + v * R} y1={CY - R - 12} x2={CX + v * R} y2={CY + R + 12} stroke="#1e1e1e" strokeWidth="1" />
                    <line x1={CX - R - 12} y1={CY - v * R} x2={CX + R + 12} y2={CY - v * R} stroke="#1e1e1e" strokeWidth="1" />
                  </g>
                ))}

                {/* Axes */}
                <line x1={16} y1={CY} x2={SVG_SIZE - 16} y2={CY} stroke="#333" strokeWidth="1.5" />
                <line x1={CX} y1={16} x2={CX} y2={SVG_SIZE - 16} stroke="#333" strokeWidth="1.5" />

                {/* Axis arrowheads */}
                <polygon points={`${SVG_SIZE - 12},${CY} ${SVG_SIZE - 20},${CY - 4} ${SVG_SIZE - 20},${CY + 4}`} fill="#333" />
                <polygon points={`${CX},12 ${CX - 4},20 ${CX + 4},20`} fill="#333" />

                {/* Axis labels */}
                <text x={SVG_SIZE - 10} y={CY + 14} fill="#444" fontSize="13" textAnchor="middle" fontStyle="italic">x</text>
                <text x={CX + 10} y={14} fill="#444" fontSize="13" textAnchor="start" fontStyle="italic">y</text>

                {/* Tick marks */}
                {[
                  { x: CX + R, y: CY, labelX: CX + R, labelY: CY + 16, text: '1', anchor: 'middle' },
                  { x: CX - R, y: CY, labelX: CX - R, labelY: CY + 16, text: '−1', anchor: 'middle' },
                  { x: CX, y: CY - R, labelX: CX + 8, labelY: CY - R + 4, text: '1', anchor: 'start' },
                  { x: CX, y: CY + R, labelX: CX + 8, labelY: CY + R + 4, text: '−1', anchor: 'start' },
                ].map((t, i) => (
                  <g key={i}>
                    <line x1={t.x - 3} y1={t.y} x2={t.x + 3} y2={t.y} stroke="#444" strokeWidth="1" />
                    <line x1={t.x} y1={t.y - 3} x2={t.x} y2={t.y + 3} stroke="#444" strokeWidth="1" />
                    <text x={t.labelX} y={t.labelY} fill="#3a3a3a" fontSize="10" textAnchor={t.anchor as 'middle' | 'start'}>{t.text}</text>
                  </g>
                ))}

                {/* Unit circle */}
                <circle cx={CX} cy={CY} r={R} fill="none" stroke="#84cc16" strokeWidth="1.5" opacity="0.35" />

                {/* Right triangle fill */}
                <polygon
                  points={`${CX},${CY} ${footX},${CY} ${footX},${py}`}
                  fill="#84cc16"
                  fillOpacity="0.05"
                />

                {/* cos projection (horizontal) */}
                <line
                  x1={CX} y1={CY}
                  x2={footX} y2={CY}
                  stroke="#22d3ee" strokeWidth="2.5" strokeLinecap="round"
                />
                <text
                  x={(CX + footX) / 2}
                  y={CY + (sinVal >= 0 ? 20 : -10)}
                  fill="#22d3ee"
                  fontSize="12"
                  textAnchor="middle"
                  fontFamily="ui-monospace, monospace"
                >
                  cos θ
                </text>

                {/* sin projection (vertical) */}
                <line
                  x1={footX} y1={CY}
                  x2={footX} y2={py}
                  stroke="#84cc16" strokeWidth="2.5" strokeLinecap="round"
                />
                <text
                  x={footX + (cosVal >= 0 ? 22 : -22)}
                  y={(CY + py) / 2 + 4}
                  fill="#84cc16"
                  fontSize="12"
                  textAnchor="middle"
                  fontFamily="ui-monospace, monospace"
                >
                  sin θ
                </text>

                {/* Radius line */}
                <line x1={CX} y1={CY} x2={px} y2={py} stroke="rgba(255,255,255,0.7)" strokeWidth="2" strokeLinecap="round" />

                {/* Angle arc */}
                {normalizedAngle > 0.05 && normalizedAngle < 6.23 && (
                  <>
                    <path d={arcPath} fill="none" stroke="#f59e0b" strokeWidth="1.5" opacity="0.85" />
                    <text
                      x={arcLabelX}
                      y={arcLabelY}
                      fill="#f59e0b"
                      fontSize="14"
                      fontStyle="italic"
                      textAnchor="middle"
                      dominantBaseline="middle"
                    >
                      θ
                    </text>
                  </>
                )}

                {/* Right-angle marker at foot */}
                {Math.abs(sinVal) > 0.07 && Math.abs(cosVal) > 0.07 && (
                  <path
                    d={`M ${footX - 8 * Math.sign(cosVal)} ${CY}
                       L ${footX - 8 * Math.sign(cosVal)} ${CY - 8 * Math.sign(sinVal)}
                       L ${footX} ${CY - 8 * Math.sign(sinVal)}`}
                    fill="none"
                    stroke="#444"
                    strokeWidth="1"
                  />
                )}

                {/* Quadrant labels */}
                <text x={CX + 58} y={CY - 58} fill="#2a2a2a" fontSize="11" textAnchor="middle">I</text>
                <text x={CX - 58} y={CY - 58} fill="#2a2a2a" fontSize="11" textAnchor="middle">II</text>
                <text x={CX - 58} y={CY + 68} fill="#2a2a2a" fontSize="11" textAnchor="middle">III</text>
                <text x={CX + 58} y={CY + 68} fill="#2a2a2a" fontSize="11" textAnchor="middle">IV</text>

                {/* Origin */}
                <circle cx={CX} cy={CY} r={3} fill="#333" />

                {/* Draggable point */}
                <circle
                  cx={px} cy={py} r={11}
                  fill="#111"
                  stroke="#84cc16"
                  strokeWidth="2.5"
                  style={{
                    cursor: isDragging ? 'grabbing' : 'grab',
                    filter: 'drop-shadow(0 0 8px rgba(132,204,22,0.6))',
                  }}
                />
                <circle cx={px} cy={py} r={4} fill="#84cc16" />
              </svg>
            </div>
          </div>

          {/* ── Right panel: explanation + table ── */}
          <div className="lg:w-64 w-full space-y-4 flex-shrink-0 order-3">

            <div className="card">
              <h3 className="font-semibold text-white text-sm mb-3 flex items-center gap-2">
                <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: '#84cc16' }} />
                Синус
              </h3>
              <p className="text-gray-400 text-sm leading-relaxed mb-3">
                Раздели <span style={{ color: '#84cc16' }}>зелёную</span> линию на <span className="text-white">белую</span> — получится синус.
              </p>

              {/* Live bar ratio */}
              <div className="space-y-1.5 mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-600 w-14 flex-shrink-0">зелёная</span>
                  <div className="h-2 rounded-full flex-1 overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
                    <div
                      className="h-full rounded-full transition-all duration-75"
                      style={{ width: `${Math.abs(sinVal) * 100}%`, background: '#84cc16' }}
                    />
                  </div>
                  <span className="text-xs font-mono w-10 text-right flex-shrink-0" style={{ color: '#84cc16' }}>
                    {Math.abs(sinVal).toFixed(2)}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-600 w-14 flex-shrink-0">белая</span>
                  <div className="h-2 rounded-full flex-1" style={{ background: 'rgba(255,255,255,0.5)' }} />
                  <span className="text-xs font-mono w-10 text-right flex-shrink-0 text-gray-400">1.00</span>
                </div>
              </div>

              <p className="text-gray-600 text-xs leading-relaxed">
                Белая — всегда 1, это радиус окружности. Поэтому синус просто равен длине зелёной.
                Зелёная идёт вверх → плюс, вниз → минус.
              </p>
            </div>

            <div className="card">
              <h3 className="font-semibold text-white text-sm mb-3 flex items-center gap-2">
                <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: '#22d3ee' }} />
                Косинус
              </h3>
              <p className="text-gray-400 text-sm leading-relaxed mb-3">
                Раздели <span style={{ color: '#22d3ee' }}>голубую</span> линию на <span className="text-white">белую</span> — получится косинус.
              </p>

              {/* Live bar ratio */}
              <div className="space-y-1.5 mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-600 w-14 flex-shrink-0">голубая</span>
                  <div className="h-2 rounded-full flex-1 overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
                    <div
                      className="h-full rounded-full transition-all duration-75"
                      style={{ width: `${Math.abs(cosVal) * 100}%`, background: '#22d3ee' }}
                    />
                  </div>
                  <span className="text-xs font-mono w-10 text-right flex-shrink-0" style={{ color: '#22d3ee' }}>
                    {Math.abs(cosVal).toFixed(2)}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-600 w-14 flex-shrink-0">белая</span>
                  <div className="h-2 rounded-full flex-1" style={{ background: 'rgba(255,255,255,0.5)' }} />
                  <span className="text-xs font-mono w-10 text-right flex-shrink-0 text-gray-400">1.00</span>
                </div>
              </div>

              <p className="text-gray-600 text-xs leading-relaxed">
                Голубая идёт вправо → плюс, влево → минус.
                Когда точка строго наверху (90°), голубая исчезает — косинус = 0.
              </p>
            </div>

            <div className="card">
              <h3 className="font-semibold text-white text-sm mb-3">Табличные значения</h3>
              <table className="w-full text-xs">
                <thead>
                  <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                    <th className="text-left pb-2 font-normal text-gray-500">θ</th>
                    <th className="pb-2 font-normal text-center" style={{ color: '#84cc16' }}>sin</th>
                    <th className="pb-2 font-normal text-center" style={{ color: '#22d3ee' }}>cos</th>
                  </tr>
                </thead>
                <tbody>
                  {NOTABLE_ANGLES.map(({ deg, sinStr, cosStr, a }) => {
                    const isActive = Math.abs(angleDeg - deg) < 4
                    return (
                      <tr
                        key={deg}
                        onClick={() => setAngle(a)}
                        className="cursor-pointer transition-colors"
                        style={{
                          borderBottom: '1px solid rgba(255,255,255,0.04)',
                          background: isActive ? 'rgba(132,204,22,0.08)' : undefined,
                        }}
                      >
                        <td className="py-1.5 font-mono text-gray-400">{deg}°</td>
                        <td className="py-1.5 font-mono text-center" style={{ color: '#84cc16' }}>{sinStr}</td>
                        <td className="py-1.5 font-mono text-center" style={{ color: '#22d3ee' }}>{cosStr}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
              <p className="text-gray-700 text-xs mt-2">← нажми, чтобы перейти к углу</p>
            </div>
          </div>
        </div>

        {/* Bottom insight block */}
        <div className="mt-8 card max-w-2xl mx-auto">
          <h3 className="font-semibold text-white mb-3">Зачем это нужно в реальной жизни?</h3>
          <div className="text-gray-400 text-sm leading-relaxed space-y-3">
            <p>
              Синус и косинус — это <strong className="text-white">язык волн и вращений</strong>.
              Звук — это синусоиды. Переменный ток — синусоида. Орбита планеты — окружность из синуса и косинуса.
            </p>
            <p>
              В программировании: анимации плавного качания, вращение 2D и 3D объектов,
              генерация волнистых поверхностей, расчёт траекторий в играх — везде используется пара (cos θ, sin θ).
            </p>
            <p>
              Тождество <strong className="text-white">sin²θ + cos²θ = 1</strong> — это просто теорема Пифагора,
              применённая к прямоугольному треугольнику внутри единичной окружности, где гипотенуза всегда равна 1.
            </p>
          </div>
        </div>

      </div>
    </div>
  )
}
